import React, { useEffect, useState } from 'react';
import { useCart } from '../../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { formatPrice } from '../../utils/formatPrice';
import { ShoppingCart, ArrowRight, Trash2, Eye, Loader2, Check, BadgeCheck } from 'lucide-react';
import { trackFunnelStep, FUNNEL_STEPS } from '../../utils/analytics';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import PageHeader from '../common/PageHeader';
import ReviewOrderDialog from './ReviewOrderDialog';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Helper function to truncate long filenames
const truncateFilename = (filename, maxLength = 30) => {
  if (!filename || filename.length <= maxLength) return filename;
  
  const extension = filename.split('.').pop();
  const nameWithoutExt = filename.slice(0, -(extension.length + 1));
  
  const halfLength = Math.floor((maxLength - extension.length - 4) / 2);
  const start = nameWithoutExt.slice(0, halfLength);
  const end = nameWithoutExt.slice(-halfLength);
  
  return `${start}...${end}.${extension}`;
};

const CartPage = () => {
  const { cart, clearCart, removeFromCart } = useCart();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedItemForPreview, setSelectedItemForPreview] = useState(null);
  const [requestDesignReview, setRequestDesignReview] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);

  useEffect(() => {
    trackFunnelStep(FUNNEL_STEPS.CART_VIEW);
  }, []);

  const handleSubmitForReview = async (customerInfo) => {
    try {
      setIsLoading(true);
      trackFunnelStep(FUNNEL_STEPS.REVIEW_REQUEST);
      
      console.log('Submitting order for design review...');
      
      // Create the order with full cart data
      // The create-order function will handle storing everything to S3
      
      // Round to nearest whole krona first, then convert to öre (same as Stripe)
      // This ensures no decimals in the final price
      const amountInKronor = Math.round(cart.total);
      const amountInOre = amountInKronor * 100;
      
      const response = await fetch(
        '/.netlify/functions/create-order',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            items: cart.items, // Send full cart items with all design data
            totalAmount: amountInOre, // Amount in öre (no decimals)
            status: 'Granskning',
            needsReview: true,
            paymentMethod: 'pending_review',
            customerInfo
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to submit order for review');
      }

      const result = await response.json();
      console.log('Order submitted for review:', result);
      
      // Close dialog
      setShowReviewDialog(false);
      
      // Clear cart and redirect to success page
      clearCart();
      navigate('/payment-success?review=true');
      
      toast.success('Din design har skickats för granskning!', {
        duration: 5000,
        position: 'top-center',
      });
    } catch (err) {
      console.error('Review submission error:', err);
      toast.error(err.message || 'Kunde inte skicka din design för granskning. Försök igen.', {
        duration: 5000,
        position: 'top-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckoutButtonClick = () => {
    if (requestDesignReview) {
      // Show dialog to collect customer info
      setShowReviewDialog(true);
    } else {
      // Proceed with normal checkout
      handleCheckout();
    }
  };

  const handleCheckout = async () => {
    try {
      setIsLoading(true);
      trackFunnelStep(FUNNEL_STEPS.CHECKOUT_START);
      const stripe = await stripePromise;
      
      console.log('Starting checkout...');
      
      const response = await fetch(
        '/.netlify/functions/create-checkout-session',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            items: cart.items
          }),
        }
      );

      // Check for any error in the response
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          console.error('Error parsing error response:', e);
          errorData = { error: errorText || 'Unknown error' };
        }
        
        const errorMessage = errorData.error || 
          `Server error (${response.status}): ${errorText || 'Unknown error'}`;
        
        console.error('Checkout error response:', errorData);
        throw new Error(errorMessage);
      }

      const sessionData = await response.json();
      console.log('Received session data', sessionData.sessionId ? '✅' : '❌');
      
      if (!sessionData.sessionId) {
        throw new Error('Invalid response from server (missing sessionId)');
      }
      
      const { error } = await stripe.redirectToCheckout({
        sessionId: sessionData.sessionId,
      });

      if (error) {
        console.error('Stripe redirect error:', error);
        throw new Error(`Payment failed: ${error.message}`);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      
      let errorMessage = err.message || 'Something went wrong. Please try again.';
      
      // Format common Stripe errors nicely for the user
      if (errorMessage.includes('metadata')) {
        errorMessage = 'Your cart contains too much data. Please try removing some items and try again.';
      } else if (errorMessage.includes('No such customer')) {
        errorMessage = 'There was a problem with the checkout system. Please try again.';
      } else if (errorMessage.includes('not a function')) {
        errorMessage = 'There was a technical issue with the checkout. Please try again or contact support.';
      } else if (errorMessage.includes('network')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      }
      
      // Show error to user with toast instead of alert
      toast.error(errorMessage, {
        duration: 5000,
        position: 'top-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Simple test checkout for debugging
  const handleTestCheckout = async () => {
    try {
      setIsLoading(true);
      console.log('Starting test checkout...');
      
      const stripe = await stripePromise;
      
      // Use the simplified checkout function
      const response = await fetch(
        '/.netlify/functions/simple-checkout',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ test: true }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          console.error('Error parsing error response:', e);
          errorData = { error: errorText || 'Unknown error' };
        }
        
        console.error('Test checkout error response:', errorData);
        throw new Error(errorData.error || `Server error (${response.status})`);
      }

      const sessionData = await response.json();
      console.log('Received test session data', sessionData);
      
      if (!sessionData.sessionId) {
        throw new Error('Invalid response from server (missing sessionId)');
      }
      
      const { error } = await stripe.redirectToCheckout({
        sessionId: sessionData.sessionId,
      });

      if (error) {
        console.error('Stripe redirect error:', error);
        throw new Error(`Payment failed: ${error.message}`);
      }
    } catch (err) {
      console.error('Test checkout error:', err);
      
      toast.error(err.message || 'Test checkout failed', {
        duration: 5000,
        position: 'top-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Test Stripe API connection directly
  const testStripeConnection = async () => {
    try {
      const response = await fetch('/.netlify/functions/diagnose-stripe');
      const data = await response.json();
      
      if (data.stripeConnected) {
        toast.success('Stripe connection successful', {
          duration: 3000,
          position: 'top-center',
        });
      } else {
        toast.error(`Stripe connection failed: ${data.error}`, {
          duration: 5000,
          position: 'top-center',
        });
      }
      
      console.log('Stripe diagnostic result:', data);
    } catch (err) {
      console.error('Error testing Stripe connection:', err);
      toast.error('Failed to test Stripe connection', {
        duration: 3000,
        position: 'top-center',
      });
    }
  };

  // Get unique files that need vectorization across all items
  const getUniqueVectorizationFiles = () => {
    const uniqueFiles = new Set();
    const fileDetails = new Map();

    cart.items.forEach(item => {
      Object.values(item.design)
        .flat()
        .filter(graphic => 
          graphic.type === 'image' && 
          graphic.name &&
          graphic.name.toLowerCase().match(/\.(png|jpg|jpeg)$/)
        )
        .forEach(file => {
          uniqueFiles.add(file.originalFileId);
          fileDetails.set(file.originalFileId, file);
        });
    });

    return {
      files: Array.from(fileDetails.values()),
      cost: uniqueFiles.size * 1500
    };
  };

  const calculatePriceBreakdown = (item) => {
    const totalWithVAT = item.totalCost;
    const totalBeforeVAT = totalWithVAT / 1.25;
    const VAT = totalWithVAT - totalBeforeVAT;

    return {
      totalWithVAT,
      totalBeforeVAT,
      VAT
    };
  };

  const handleEditDesign = (itemId) => {
    navigate(`/customize?edit=${itemId}`);
  };

  // Function to open the preview modal
  const handleOpenPreview = (item) => {
    setSelectedItemForPreview(item);
    setPreviewModalOpen(true);
  };

  if (!cart?.items?.length) {
    return (
      <div className="h-screen flex flex-col overflow-hidden bg-secondary/50">
        <PageHeader />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md w-full">
            <Card>
              <CardContent className="p-8">
                <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-2xl font-semibold mb-4">Din varukorg är tom</h2>
                <p className="text-muted-foreground mb-6">
                  Du har inte lagt till några dekaler i din varukorg än. 
                  Börja designa dina egna unika dekaler nu!
                </p>
                <Button
                  onClick={() => navigate('/customize')}
                  className="w-full"
                  size="lg"
                >
                  <span>Designa dekaler</span>
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader />
      <div className="min-h-screen bg-secondary/50 p-4 md:p-8">
        <div className="max-w-6xl mx-auto cart-wrapper">
          <div className="mb-8">
            <h1 className="text-3xl font-bold">Din varukorg</h1>
          </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item, index) => {
              const { files: uniqueFiles, cost: totalVectorizationCost } = getUniqueVectorizationFiles();
              const { totalWithVAT, totalBeforeVAT, VAT } = calculatePriceBreakdown(item);

              return (
                <Card key={index}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      {/* Image */}
                      <div className="relative flex-shrink-0">
                        {item.previewImage ? (
                          <img 
                            src={item.previewImage} 
                            alt={`Design preview for ${item.car.name}`}
                            className="w-32 h-auto object-contain cursor-pointer rounded"
                            onClick={() => handleOpenPreview(item)}
                          />
                        ) : item.previewImages && item.previewImages[Object.keys(item.previewImages)[0]] ? (
                          <img 
                            src={item.previewImages[Object.keys(item.previewImages)[0]]} 
                            alt={`Design preview for ${item.car.name}`}
                            className="w-32 h-auto object-contain cursor-pointer rounded"
                            onClick={() => handleOpenPreview(item)}
                          />
                        ) : (
                          <img
                            src={item.car.previewImage}
                            alt={item.car.name}
                            className="w-32 h-auto object-contain rounded"
                          />
                        )}
                        
                        {/* View all button */}
                        {item.previewImages && Object.keys(item.previewImages).length > 1 && (
                          <Button 
                            variant="secondary"
                            size="sm"
                            onClick={() => handleOpenPreview(item)}
                            className="absolute bottom-2 right-2 h-7 text-xs"
                          >
                            <Eye size={14} className="mr-1" />
                            Visa
                          </Button>
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{item.car.name}</h3>
                            <p className="text-sm text-muted-foreground">
                              {Object.values(item.design).flat().length} dekaler
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeFromCart(item.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 size={20} />
                          </Button>
                        </div>

                        <Separator className="my-4" />

                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Antal</span>
                            <span className="font-medium">{item.quantity} st</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Delsumma</span>
                            <span className="font-medium">{formatPrice(totalBeforeVAT)} kr</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Moms (25%)</span>
                            <span className="font-medium">{formatPrice(VAT)} kr</span>
                          </div>
                          
                          <Separator className="my-2" />
                          
                          <div className="flex justify-between">
                            <span className="font-semibold">Totalt</span>
                            <span className="font-semibold text-lg">{formatPrice(totalWithVAT)} kr</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Design Review Option */}
            <Card className="border-2 border-primary/20">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4 w-full">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <BadgeCheck className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold text-lg">Vill du få din bil design granskad?</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Vi går igenom helheten – placering, balans och läsbarhet – innan produktion.
                    </p>
                    
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Expert går igenom hela bildesignen</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Kontroll av placering och proportioner</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Feedback på läsbarhet och synlighet</span>
                      </li>
                    </ul>

                    {requestDesignReview && (
                      <Alert className="bg-green-50 border-green-200 w-full">
                        <Check className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-900">
                          Gratis! Din design kommer granskas innan produktion. Vi skickar betalningslänk efter godkänd granskning.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                  
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <div className="text-right mb-2 whitespace-nowrap">
                      <div className="text-sm font-semibold text-primary">GRATIS</div>
                      <div className="text-xs text-muted-foreground">Ingen extra kostnad</div>
                    </div>
                    <Switch
                      checked={requestDesignReview}
                      onCheckedChange={setRequestDesignReview}
                      className="data-[state=checked]:bg-primary"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Ordersammanfattning</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Antal dekaler</span>
                    <span className="font-medium">
                      {cart.items.reduce((sum, item) => 
                        sum + (Object.values(item.design).flat().length * item.quantity), 0
                      )} st
                    </span>
                  </div>

                  {cart.items.some(item => item.needsInstallation) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Montering av dekaler</span>
                      <span className="font-medium text-primary">2 500 kr</span>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Delsumma</span>
                    <span className="font-medium">{formatPrice(cart.total / 1.25)} kr</span>
                  </div>
                  
                  {/* Vectorization info - shown as included in price */}
                  {(() => {
                    const { files: uniqueFiles, cost: totalVectorizationCost } = getUniqueVectorizationFiles();
                    if (uniqueFiles.length > 0) {
                      return (
                        <div className="text-xs text-muted-foreground pl-4 -mt-1">
                          <span className="italic">Inkl. vektorisering ({uniqueFiles.length} {uniqueFiles.length === 1 ? 'fil' : 'filer'}, {formatPrice(totalVectorizationCost)} kr)</span>
                          <ul className="mt-1 pl-2 space-y-0.5">
                            {uniqueFiles.map(file => (
                              <li key={file.id} className="truncate" title={file.name}>
                                • {truncateFilename(file.name, 32)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Moms (25%)</span>
                    <span className="font-medium">{formatPrice(cart.total - (cart.total / 1.25))} kr</span>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex justify-between">
                    <span className="font-semibold text-lg">Totalt</span>
                    <span className="font-semibold text-lg">{formatPrice(cart.total)} kr</span>
                  </div>
                </div>

                <Button
                  onClick={handleCheckoutButtonClick}
                  disabled={isLoading}
                  className="w-full"
                  size="lg"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      <span>Laddar...</span>
                    </>
                  ) : requestDesignReview ? (
                    <>
                      <span>Skicka för granskning</span>
                      <ArrowRight className="ml-2" size={20} />
                    </>
                  ) : (
                    <>
                      <span>Betala</span>
                      <ArrowRight className="ml-2" size={20} />
                    </>
                  )}
                </Button>
                
                {process.env.NODE_ENV === 'development' && (
                  <div className="pt-4 border-t space-y-2">
                    <p className="text-xs text-muted-foreground uppercase font-medium">Utvecklingsverktyg</p>
                    <Button 
                      variant="outline"
                      onClick={testStripeConnection}
                      className="w-full"
                      size="sm"
                    >
                      Testa Stripe anslutning
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleTestCheckout}
                      className="w-full"
                      size="sm"
                    >
                      Testa enkel checkout
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle>
              {selectedItemForPreview?.car.name} - Alla vyer
            </DialogTitle>
          </DialogHeader>
          
          {selectedItemForPreview && (() => {
            const viewOrder = ['Sida Vänster', 'Sida Höger', 'Fram', 'Bak', 'Topp'];
            const viewLabels = {
              'Sida Vänster': 'Sida Vänster',
              'Sida Höger': 'Sida Höger',
              'Fram': 'Fram',
              'Bak': 'Bak',
              'Topp': 'Topp'
            };
            const availableViews = viewOrder.filter(view => 
              selectedItemForPreview.previewImages && selectedItemForPreview.previewImages[view]
            );
            const defaultView = availableViews.includes('Sida Vänster') ? 'Sida Vänster' : availableViews[0];

            return (
              <Tabs defaultValue={defaultView} className="w-full p-6 pt-0">
                <TabsList className="w-full justify-start overflow-x-auto">
                  {availableViews.map(view => (
                    <TabsTrigger key={view} value={view}>
                      {viewLabels[view] || view}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {availableViews.map(view => (
                  <TabsContent key={view} value={view} className="mt-4">
                    <div className="flex items-center justify-center bg-muted rounded-lg p-4">
                      <img 
                        src={selectedItemForPreview.previewImages[view]} 
                        alt={`${selectedItemForPreview.car.name} - ${viewLabels[view] || view}`}
                        className="max-h-[60vh] object-contain rounded-lg"
                        onContextMenu={(e) => e.preventDefault()}
                        draggable="false"
                      />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Review Order Dialog */}
      <ReviewOrderDialog
        isOpen={showReviewDialog}
        onClose={() => setShowReviewDialog(false)}
        onSubmit={handleSubmitForReview}
        isLoading={isLoading}
      />
    </div>
    </>
  );
};

export default CartPage; 